import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useHovered } from '../hooks/useHovered/useHovered';
import clsx from 'clsx';

import { Game } from 'modules/lobby-page/pages/lobby';

import { OptimizedImage } from 'shared/components/ui/optimized-image/optimized-image';
import { WatchBtn } from 'shared/components/ui/watch-btn/watch-btn';
import { coinIcons } from 'shared/constants';
import { useAccount } from 'shared/hooks/use-web3';
import useMediaQuery from 'shared/hooks/useMediaQuery';
import { useCurrenciesStore } from 'shared/store/currenciesStore';
import { useGlobalStore } from 'shared/store/globalStore';
import { prettyTx } from 'shared/utils/pretty-tx';

import EyeIcon from 'assets/eye.svg?react';
import WatchGameIcon from 'assets/png/replay.png?format=webp;avif&as=picture';

interface IWatchGameItemProps {
  game: Game;
}

export const WatchGameItem = ({ game }: IWatchGameItemProps) => {
  const navigate = useNavigate();
  const { chainId } = useAccount();
  const { leaderboardMap } = useGlobalStore();
  const { availableCurrencies } = useCurrenciesStore();
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const isTablet = useMediaQuery('(max-width: 768px)');
  const { isItemHovered, handleShowButton, handleHideButton } = useHovered(containerRef, isTablet);
  const currency = availableCurrencies.find(
    (currency) => currency.address.toLowerCase() === game[3].toLowerCase(),
  );

  const normalizedAmount = game[4];

  const playerA = useMemo(() => {
    return prettyTx({
      address: game[1],
      leaderboardMap: leaderboardMap,
      startAddressLength: 2,
      endAddressLength: 4,
      discordUsernameLength: 9,
    });
  }, [game, leaderboardMap]);
  const playerB = useMemo(() => {
    return prettyTx({
      address: game[2],
      leaderboardMap: leaderboardMap,
      startAddressLength: 2,
      endAddressLength: 4,
      discordUsernameLength: 9,
    });
  }, [game, leaderboardMap]);
  return (
    <div
      ref={containerRef}
      className="w-full"
      onMouseOver={handleShowButton}
      onMouseOut={handleHideButton}
    >
      <div
        className={`grid grid-cols-[2fr_1fr_1fr_1.5fr] rounded-md border border-[#FFC000] border-[#FFFFFF40] bg-gradient-to-r from-[#05055B] to-[#151552] p-1.5 transition-all sm:rounded-2xl sm:border-2`}
      >
        <div className="items flex items-center gap-[12px]">
          <OptimizedImage
            imageData={WatchGameIcon}
            alt="short"
            className="h-5 w-5 sm:h-[60px] sm:w-[60px]"
          />
          <div className="flex flex-col">
            <span
              className={clsx(
                'min-w-[9.75rem] font-chivo text-[9px] font-medium leading-none text-yellow sm:text-[20px]',
              )}
            >
              {playerA}
            </span>
            <span className="pl-8 text-[9px] font-medium leading-none text-white opacity-75 sm:text-[14px]">
              {t('common.vs')}
            </span>
            <span
              className={clsx(
                'min-w-[9.75rem] font-chivo text-[9px] font-medium leading-none text-[#4FB5FF] sm:text-[20px]',
              )}
            >
              {playerB}
            </span>
          </div>
        </div>
        <div className="flex items-center justify-center gap-2">
          {currency?.symbol ? (
            <img
              src={coinIcons[currency.symbol as keyof typeof coinIcons]}
              alt="coin"
              className="h-9 w-9"
            />
          ) : null}
          <div>
            <p className="px-0.5 text-[20px] font-semibold text-white opacity-75">
              {parseFloat(normalizedAmount).toFixed(2)}
            </p>

            <p className="text-base font-semibold text-white opacity-50">{currency?.symbol}</p>
          </div>
        </div>

        <div className="relative flex items-center justify-center gap-2">
          {/* hidden while we do not have this games from api */}
          {/* <OptimizedImage imageData={Heart} alt="heart" className="h-7 w-7" /> */}
          {/* <OptimizedImage
                        imageData={Heart}
                        alt="heart"
                        className={`h-7 w-7 ${wins > 3 ? 'opacity-50' : ''}`}
                    /> */}

          {/* {wins !== 3 && wins > 0 && (
                        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[36px] font-semibold leading-none text-white">
                            {wins}
                        </span>
                    )} */}
          {/* <OptimizedImage imageData={Heart} alt="heart" className="h-7 w-7" /> */}
        </div>

        <div className="flex items-center justify-end">
          {isItemHovered ? (
            <WatchBtn
              svg={<EyeIcon className="h-6 w-6 text-[#2E083D]" />}
              onClick={() => {
                navigate(`/stream/${chainId}/${game[0]}`);
              }}
              text="common.watchGame"
              className="w-[192px]"
            />
          ) : (
            <p className="mr-5 flex items-center gap-2 whitespace-nowrap text-[20px] font-semibold text-white/50">
              <EyeIcon className="h-6 w-6 opacity-25" />
              {t('common.watchGame')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
